import { useTranslation } from 'react-i18next'
import Big from 'big.js'
import useActiveOrDefaultChain from 'src/common/hooks/web3/useActiveOrDefaultChain'
import { hasPriceFeed } from 'src/common/queries/usePriceFeeds/utils'
import Icon from 'src/common/ui-kit/Icon/Icon'
import NumericField from 'src/common/ui-kit/NumericField/NumericField'
import Switch from 'src/common/ui-kit/Switch/Switch'
import TokenIcon from 'src/common/ui-kit/TokenIcon/TokenIcon'
import Tooltip from 'src/common/ui-kit/Tooltip/Tooltip'
import { displayMinThresold } from 'src/common/utils/formatters/number'
import PriceBadges from './PriceBadges'
import { AnyToken } from '../../types/models/Tokens'
import { DotcEnums } from 'src/common/contracts/enums/dotc'

export interface PricePerTokenProps {
  sellToken: AnyToken
  buyToken: AnyToken
  price: Big
  percentage: number | null
  percentageType: DotcEnums.PercentageType | null
  isDynamic: boolean
  isSwappedPrice: boolean
  showOfferTakingType?: boolean
  showPercentage?: boolean
  onChangePercentage: (value: number | null) => void
  onChangePercentageType?: (value: DotcEnums.PercentageType | null) => void
  onChangePrice: (value: Big) => void
  swapPrice: () => void
  onChangeSellAmount?: (value: Big) => void
  onChangeDynamic?: (value: boolean) => void
}

const PricePerToken = ({
  sellToken,
  buyToken,
  price,
  percentage,
  percentageType,
  isDynamic,
  isSwappedPrice,
  showOfferTakingType = true,
  showPercentage = true,
  onChangePercentage,
  onChangePercentageType,
  onChangePrice,
  swapPrice,
  onChangeDynamic,
}: PricePerTokenProps) => {
  const { t } = useTranslation('common')
  const { id: chainId } = useActiveOrDefaultChain()

  const tokenIn = isSwappedPrice ? buyToken : sellToken
  const tokenOut = isSwappedPrice ? sellToken : buyToken

  const canDynamic =
    hasPriceFeed(sellToken?.contractAddress, chainId) &&
    hasPriceFeed(buyToken?.contractAddress, chainId)

  return (
    <div className='paper p-4 flex flex-col gap-2'>
      <div className='flex items-center justify-between'>
        <div className='flex items-center gap-2'>
          <div className='typo-label2'>{t('pricePerToken.pricePer')}</div>
          <div className='flex items-center typo-label2'>
            <TokenIcon className='mr-1' token={tokenIn} /> 1 {tokenIn?.symbol}
          </div>
          <div
            onClick={swapPrice}
            className='rounded p-0.5 bg-secondary hover:bg-secondary-accent cursor-pointer'
          >
            <Icon size={14} name='exchange' color='secondary-contrast' />
          </div>
        </div>

        <div>
          {showOfferTakingType && (
            <div className='flex justify-between'>
              <div className='flex items-center'>
                <span className='typo-label1 text-main-accent mr-2'>
                  {isDynamic
                    ? t('pricePerToken.dynamic')
                    : t('pricePerToken.fixed')}
                </span>
                <Switch
                  disabled={!canDynamic}
                  className='mr-1'
                  checked={isDynamic}
                  onChange={onChangeDynamic}
                />
                <Tooltip
                  content={t(
                    'Dynamic offers set prices to float at a percentage above market price (max 100%), available only for assets with price feeds'
                  )}
                >
                  <Icon size='20px' name='info' />
                </Tooltip>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className='flex justify-between items-center'>
        <NumericField
          type='big'
          className='col-start-8 col-span-5'
          value={price}
          displayValue={displayMinThresold(price)}
          onChange={onChangePrice}
          variant='text'
          size='lg'
          hideError
          inputProps={{
            className: 'pl-0',
          }}
        />
        <div className='flex items-center gap-2 min-w-fit'>
          <TokenIcon token={tokenOut} />
          <div className='typo-label2 text-main-accent'>{tokenOut?.symbol}</div>
        </div>
      </div>
      {showPercentage && (
        <PriceBadges
          className='flex gap-2'
          percentage={percentage}
          percentageType={percentageType}
          onChange={onChangePercentage}
          isDynamic={isDynamic}
          onChangePercentageType={onChangePercentageType}
        />
      )}
    </div>
  )
}

export default PricePerToken
